import React, { useMemo, useState } from 'react';
import { alpha, Box, Button, Slider, Stack, TextField, Typography, useTheme } from '@mui/material';
import { SlipOutcome } from '../services/api';
import {
  chanceOfAtLeast,
  dollars,
  histogramEdges,
  likelyOutcomes,
  money,
  pct,
  PricedBet,
  profitHistogram,
  riskFrontier,
  slipStats,
  SlipStats,
  stakeCandidates,
} from '../utils/betSlip';
import { useChartTokens, useWidth } from './charts/chartKit';
import { SegmentedToggle } from './SegmentedToggle';

// The Slip Builder's exploring tabs: what if, each bet's part, stake sliders, the risk trade-off and staking to a
// goal. All of them work from the one check's simulated races (which bets hit in each), so nothing re-queries.

export type ExploreTab = 'whatif' | 'contribution' | 'stakes' | 'risk' | 'goals';

interface ExploreProps {
  outcomes: SlipOutcome[];
  /** The bets at their current stakes. */
  bets: PricedBet[];
  labels: string[];
  /** The money the slip you checked put down (suggestions stake up to this). */
  budget: number;
  /** Exposure group per bet (bets that win and lose together share one 40% limit). */
  groups: number[];
  /** Bets the suggestions may use (value or too close to call; never overpriced). */
  usable: boolean[];
  /** Put these stakes in the slip editor (the Overview tab then shows them). */
  onApply: (stakes: number[]) => void;
  /** Comparing models: each model's outcomes for the same bets (then `outcomes` is Base's). */
  compare?: { base: SlipOutcome[]; layered: SlipOutcome[] };
}

/** The two models' chart colors (validated categorical palette: blue and violet), for swatches and lines. */
export const useModelColors = () => {
  const k = useChartTokens();
  return { base: k.series[0], layered: k.series[6] };
};

export const Swatch: React.FC<{ c: string }> = ({ c }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: 9,
      height: 9,
      borderRadius: '50%',
      bgcolor: c,
      mr: 0.75,
      verticalAlign: 'middle',
    }}
  />
);

/** The models a tab shows: the one model, or both when comparing. */
type ModelView = { key: 'shown' | 'base' | 'layered'; name: string; outcomes: SlipOutcome[]; color?: string };
const useModelViews = ({ outcomes, compare }: ExploreProps): ModelView[] => {
  const c = useModelColors();
  return compare
    ? [
        { key: 'base', name: 'Base', outcomes: compare.base, color: c.base },
        { key: 'layered', name: 'Track Specialists', outcomes: compare.layered, color: c.layered },
      ]
    : [{ key: 'shown', name: '', outcomes }];
};

const withStakes = (bets: PricedBet[], st: number[]) => bets.map((b, i) => ({ ...b, stake: st[i] ?? 0 }));
const stakeStep = (budget: number) => (budget >= 50 ? 5 : 1);

/** Stat tiles: one row per model (a colored dot marks each when comparing), each with its change against a
 * comparison slip when one is given. */
const Tiles: React.FC<{
  rows: { s: SlipStats; base?: SlipStats; color?: string; name?: string }[];
  lead?: { label: string; value: string };
}> = ({ rows, lead }) => {
  const delta = (a: number, b: number | undefined, fmt: (v: number) => string) =>
    b === undefined || Math.abs(a - b) < 0.005 ? null : (
      <Typography component="span" variant="caption" sx={{ ml: 0.75, color: a > b ? 'success.main' : 'error.main' }}>
        {a > b ? '▲' : '▼'} {fmt(Math.abs(a - b))}
      </Typography>
    );
  const items: [string, (s: SlipStats) => number, (v: number) => string, (v: number) => string][] = [
    ['Expected profit', (x) => x.ev, money, dollars],
    ['Chance of profit', (x) => x.pProfit, pct, (v) => `${Math.round(v * 100)} pts`],
    ['Typical result', (x) => x.p50, money, dollars],
    ['Worst case', (x) => x.worst, money, dollars],
  ];
  // Comparing models: a key for the colored dots, above the tiles.
  const legend = rows.length > 1 && rows.every((r) => r.color && r.name);
  return (
    <>
      {legend && (
        <Stack direction="row" spacing={2} sx={{ mb: 0.75 }}>
          {rows.map((r) => (
            <Typography key={r.name} variant="caption">
              <Swatch c={r.color!} />
              {r.name}
            </Typography>
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        {lead && (
          <Box sx={{ flex: '1 1 130px', p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {lead.label}
            </Typography>
            <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{lead.value}</Typography>
          </Box>
        )}
        {items.map(([label, get, fmt, dfmt]) => (
          <Box key={label} sx={{ flex: '1 1 130px', p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            {rows.map((r, i) => (
              <Typography key={i} sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {r.color && <Swatch c={r.color} />}
                {fmt(get(r.s))}
                {delta(get(r.s), r.base && get(r.base), dfmt)}
              </Typography>
            ))}
          </Box>
        ))}
      </Stack>
    </>
  );
};

/**
 * Background for charts that draw models as lines (where color means the model, not profit or loss): the losing side
 * tinted orange and the winning side green, each labeled, matching the bar charts' orange/green.
 */
export const ProfitSides: React.FC<{ x0: number; xZero: number; x1: number; H: number }> = ({ x0, xZero, x1, H }) => {
  const k = useChartTokens();
  const theme = useTheme();
  const loss = k.dark ? '#d95926' : '#eb6834';
  const gain = theme.palette.primary.main;
  const zero = Math.min(Math.max(xZero, x0), x1);
  return (
    <g>
      <rect x={x0} y={0} width={Math.max(0, zero - x0)} height={H} fill={alpha(loss, k.dark ? 0.12 : 0.08)} />
      <rect x={zero} y={0} width={Math.max(0, x1 - zero)} height={H} fill={alpha(gain, k.dark ? 0.1 : 0.07)} />
      {zero - x0 > 70 && (
        <text x={x0 + 6} y={12} fontSize={10.5} fill={loss}>
          lose money
        </text>
      )}
      {x1 - zero > 70 && (
        <text x={x1 - 6} y={12} fontSize={10.5} textAnchor="end" fill={gain}>
          make money
        </text>
      )}
    </g>
  );
};

/** Each model's results in a set of races as a line (comparing models; colors as the swatches). */
const ScenarioLines: React.FC<{ views: ModelView[]; subs: SlipOutcome[][]; bets: PricedBet[] }> = ({
  views,
  subs,
  bets,
}) => {
  const k = useChartTokens();
  const { ref, width } = useWidth(280, 640);
  const H = 120;
  const edges = histogramEdges(
    views.flatMap((v) => v.outcomes),
    [bets],
    22,
  );
  const hists = subs.map((o) => profitHistogram(o, bets, edges));
  const max = Math.max(...hists.flat()) * 1.1 || 1;
  const n = edges.length - 1;
  const bw = (width - 20) / n;
  const x = (v: number) => 10 + ((v - edges[0]) / (edges[n] - edges[0])) * (width - 20);
  const step = (h: number[]) =>
    h.map((v, i) => `${10 + bw * i},${H - (v / max) * H} ${10 + bw * (i + 1)},${H - (v / max) * H}`).join(' ');
  return (
    <Box ref={ref}>
      <svg width={width} height={H + 18} style={{ display: 'block' }} role="img" aria-label="Profit or loss by model">
        <ProfitSides x0={x(edges[0])} xZero={x(0)} x1={x(edges[n])} H={H} />
        {hists.map((h, i) => (
          <polyline key={i} points={step(h)} fill="none" stroke={views[i].color} strokeWidth={2} />
        ))}
        <line x1={x(0)} x2={x(0)} y1={0} y2={H} stroke={k.axisText} />
        {[edges[0], 0, edges[n]].map((d, i) => (
          <text
            key={i}
            x={x(d)}
            y={H + 14}
            textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
            fontSize={11}
            fill={k.axisText}
          >
            {d === 0 ? '$0' : money(d)}
          </text>
        ))}
      </svg>
    </Box>
  );
};

/** Profit bars for a set of races, with an optional dashed outline of another set (e.g. the whole slip). */
const ScenarioBars: React.FC<{
  outcomes: SlipOutcome[];
  ghost?: SlipOutcome[];
  bets: PricedBet[];
  all: SlipOutcome[];
}> = ({ outcomes, ghost, bets, all }) => {
  const k = useChartTokens();
  const theme = useTheme();
  const { ref, width } = useWidth(280, 640);
  const [hover, setHover] = useState<number | null>(null);
  const H = 120;
  const edges = histogramEdges(all, [bets], 22);
  const bars = profitHistogram(outcomes, bets, edges);
  const ghostBars = ghost ? profitHistogram(ghost, bets, edges) : null;
  const max = Math.max(...bars, ...(ghostBars ?? [0])) * 1.1 || 1;
  const n = bars.length;
  const bw = (width - 20) / n;
  const x = (v: number) => 10 + ((v - edges[0]) / (edges[n] - edges[0])) * (width - 20);
  const loss = k.dark ? '#d95926' : '#eb6834';
  return (
    <Box ref={ref} sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={H + 18} style={{ display: 'block' }}>
        {ghostBars?.map(
          (v, i) =>
            v > 0 && (
              <rect
                key={`g${i}`}
                x={10 + bw * i + 1}
                width={Math.max(1, bw - 2)}
                y={H - (v / max) * H}
                height={(v / max) * H}
                fill="none"
                stroke={k.axisText}
                strokeDasharray="3 2"
              />
            ),
        )}
        {bars.map(
          (v, i) =>
            v > 0 && (
              <rect
                key={i}
                x={10 + bw * i + 1}
                width={Math.max(1, bw - 2)}
                y={H - (v / max) * H}
                height={(v / max) * H}
                rx={2}
                fill={edges[i] < 0 ? loss : theme.palette.primary.main}
                opacity={hover === null || hover === i ? 1 : 0.6}
              />
            ),
        )}
        <line x1={x(0)} x2={x(0)} y1={0} y2={H} stroke={k.axisText} />
        {[edges[0], 0, edges[n]].map((d, i) => (
          <text
            key={i}
            x={x(d)}
            y={H + 14}
            textAnchor={i === 0 ? 'start' : i === 2 ? 'end' : 'middle'}
            fontSize={11}
            fill={k.axisText}
          >
            {d === 0 ? '$0' : money(d)}
          </text>
        ))}
        {/* Hit targets: the full column height, wider than thin bars. */}
        {bars.map((_, i) => (
          <rect
            key={`h${i}`}
            x={10 + bw * i}
            width={bw}
            y={0}
            height={H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>
      {hover !== null && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: Math.min(Math.max(0, 10 + bw * hover - 60), width - 170),
            width: 170,
            p: 0.75,
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {money(edges[hover])} to {money(edges[hover + 1])}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {pct(bars[hover])} of these races{ghostBars ? ` · ${pct(ghostBars[hover])} overall` : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** 1. What if: the slip in only the races where chosen bets hit or miss. */
export const WhatIfTab: React.FC<ExploreProps> = (props) => {
  const { outcomes, bets, labels } = props;
  const views = useModelViews(props);
  const [cond, setCond] = useState<string[]>(() => bets.map(() => 'any'));
  const keep = (o: SlipOutcome[]) =>
    o.filter((x) => cond.every((c, i) => c === 'any' || (c === 'hit') === !!(x.hitMask & (1 << i))));
  const shareOf = (sub: SlipOutcome[], all: SlipOutcome[]) =>
    sub.reduce((t, o) => t + o.sims, 0) / (all.reduce((t, o) => t + o.sims, 0) || 1);
  const subs = views.map((v) => keep(v.outcomes));
  const shares = views.map((v, i) => shareOf(subs[i], v.outcomes));
  const sub = subs[0];
  const share = Math.max(...shares);
  const active = cond.some((c) => c !== 'any');
  const fmtShare = (x: number) => (x < 0.01 ? 'under 1%' : pct(x));
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Set any bet to hits or misses to see the slip in just those races.
      </Typography>
      <Stack spacing={0.75} sx={{ mb: 2 }}>
        {bets.map((b, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, opacity: b.stake > 0 ? 1 : 0.6 }}>
              {labels[i]}
              {b.stake > 0 ? '' : ' (not staked)'}
            </Typography>
            <SegmentedToggle
              aria-label={`${labels[i]}: what if`}
              size="small"
              value={cond[i]}
              onChange={(v: string) => setCond((c) => c.map((x, k) => (k === i ? v : x)))}
              options={[
                { value: 'any', label: 'Either' },
                { value: 'hit', label: 'Hits' },
                { value: 'miss', label: 'Misses' },
              ]}
            />
          </Stack>
        ))}
      </Stack>
      {share === 0 ? (
        <Typography variant="body2" color="warning.main">
          That combination never came up in the simulated races.
        </Typography>
      ) : (
        <>
          {active &&
            (props.compare ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                This happens in <Swatch c={views[0].color!} />
                <b>{fmtShare(shares[0])}</b> of races under Base and <Swatch c={views[1].color!} />
                <b>{fmtShare(shares[1])}</b> under Track Specialists. Then:
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ mb: 1 }}>
                This happens in <b>{fmtShare(share)}</b> of races. Then:
              </Typography>
            ))}
          <Tiles
            rows={views.map((v, i) => ({
              s: slipStats(subs[i].length ? subs[i] : v.outcomes, bets),
              base: active ? slipStats(v.outcomes, bets) : undefined,
              color: v.color,
              name: v.name,
            }))}
          />
          <Box sx={{ mt: 1.5 }}>
            {props.compare ? (
              <ScenarioLines views={views} subs={active ? subs : views.map((v) => v.outcomes)} bets={bets} />
            ) : (
              <ScenarioBars outcomes={sub} ghost={active ? outcomes : undefined} bets={bets} all={outcomes} />
            )}
            <Typography variant="caption" color="text.secondary">
              {props.compare
                ? `Lines: results ${active ? 'in these races ' : ''}under each model (orange side = losing money, green side = making money).${active ? ' Arrows compare with the whole slip under the same model.' : ''}`
                : active
                  ? 'Bars: results in these races. Dashed outline: the whole slip. Arrows compare with the whole slip.'
                  : 'Profit or loss across all the simulated races (orange = lose money, green = make money).'}
            </Typography>
          </Box>
        </>
      )}
    </>
  );
};

/** 2. Each bet's part: what the slip would look like without it. */
export const ContributionTab: React.FC<ExploreProps> = (props) => {
  const { outcomes, bets, labels } = props;
  const views = useModelViews(props);
  const all = slipStats(outcomes, bets);
  const without = (o: SlipOutcome[], i: number) =>
    slipStats(
      o,
      bets.map((x, k) => (k === i ? { ...x, stake: 0 } : x)),
    );
  const rows = bets
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => b.stake > 0)
    .map(({ b, i }) => {
      const s = without(outcomes, i);
      return {
        b,
        i,
        d: { ev: s.ev - all.ev, pp: s.pProfit - all.pProfit, p50: s.p50 - all.p50, worst: s.worst - all.worst },
        // Comparing: the change in each model, for expected profit, chance of profit and worst case.
        both: views.map((v) => {
          const a = slipStats(v.outcomes, bets);
          const w = without(v.outcomes, i);
          return { ev: w.ev - a.ev, pp: w.pProfit - a.pProfit, worst: w.worst - a.worst };
        }),
      };
    });
  const cell = (v: number, fmt: (x: number) => string) => (
    <Typography
      variant="body2"
      sx={{
        fontVariantNumeric: 'tabular-nums',
        color: Math.abs(v) < 0.005 ? 'text.secondary' : v > 0 ? 'success.main' : 'error.main',
      }}
    >
      {Math.abs(v) < 0.005 ? '—' : `${v > 0 ? '+' : '−'}${fmt(Math.abs(v))}`}
    </Typography>
  );
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        What the slip would look like without each bet. Green means the slip would be better on that measure without it.
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            minWidth: 520,
            borderCollapse: 'collapse',
            '& td, & th': { py: 0.75, px: 1, borderBottom: 1, borderColor: 'divider', textAlign: 'right' },
            '& th': { color: 'text.secondary', fontWeight: 500, fontSize: 13 },
            '& td:first-of-type, & th:first-of-type': { textAlign: 'left' },
          }}
        >
          <thead>
            {props.compare ? (
              <>
                <tr>
                  <th rowSpan={2}>Without this bet…</th>
                  <th colSpan={2}>Expected</th>
                  <th colSpan={2}>Chance of profit</th>
                  <th colSpan={2}>Worst case</th>
                </tr>
                <tr>
                  {[0, 1, 2].map((g) =>
                    views.map((v) => (
                      <th key={`${g}${v.key}`} title={v.name}>
                        <Swatch c={v.color!} />
                        {v.key === 'base' ? 'Base' : 'TS'}
                      </th>
                    )),
                  )}
                </tr>
              </>
            ) : (
              <tr>
                <th>Without this bet…</th>
                <th>Expected</th>
                <th>Chance of profit</th>
                <th>Typical</th>
                <th>Worst case</th>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.i}>
                <td>
                  <Typography variant="body2">
                    {labels[r.i]}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({dollars(r.b.stake)})
                    </Typography>
                  </Typography>
                </td>
                {props.compare ? (
                  <>
                    {r.both.map((d, j) => (
                      <td key={`ev${j}`}>{cell(d.ev, dollars)}</td>
                    ))}
                    {r.both.map((d, j) => (
                      <td key={`pp${j}`}>{cell(d.pp * 100, (x) => `${x.toFixed(0)} pts`)}</td>
                    ))}
                    {r.both.map((d, j) => (
                      <td key={`w${j}`}>{cell(d.worst, dollars)}</td>
                    ))}
                  </>
                ) : (
                  <>
                    <td>{cell(r.d.ev, dollars)}</td>
                    <td>{cell(r.d.pp * 100, (x) => `${x.toFixed(0)} pts`)}</td>
                    <td>{cell(r.d.p50, dollars)}</td>
                    <td>{cell(r.d.worst, dollars)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Box>
      </Box>
      {props.compare ? (
        views.map((v) => {
          const a = slipStats(v.outcomes, bets);
          return (
            <Typography key={v.key} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              <Swatch c={v.color!} />
              The whole slip under {v.name}: expected {money(a.ev)}, chance of profit {pct(a.pProfit)}, worst{' '}
              {money(a.worst)}.
            </Typography>
          );
        })
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The whole slip: expected {money(all.ev)}, chance of profit {pct(all.pProfit)}, typical {money(all.p50)}, worst{' '}
          {money(all.worst)}.
        </Typography>
      )}
    </>
  );
};

/** 3. Stake sliders with the results updating as you drag. */
export const StakesTab: React.FC<ExploreProps> = (props) => {
  const { bets, labels, budget, onApply } = props;
  const views = useModelViews(props);
  const start = bets.map((b) => b.stake);
  const [st, setSt] = useState(start);
  const step = stakeStep(budget);
  const max = Math.max(step * 4, Math.ceil(Math.max(budget * 0.6, ...start) / step) * step);
  const total = st.reduce((t, v) => t + v, 0);
  const changed = st.join() !== start.join();
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Drag any stake and the numbers update as you go. Arrows compare with the stakes in your slip
        {props.compare ? ' under the same model' : ''}.
      </Typography>
      <Stack spacing={{ xs: 1, sm: 0.25 }} sx={{ mb: 2 }}>
        {bets.map((_, i) => (
          <Stack
            key={i}
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={{ xs: 0, sm: 2 }}
          >
            <Typography variant="body2" noWrap title={labels[i]} sx={{ width: { sm: 260 }, flexShrink: 0 }}>
              {labels[i]}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
              <Slider
                size="small"
                min={0}
                max={max}
                step={step}
                value={st[i]}
                onChange={(_, v) => setSt((s) => s.map((x, k) => (k === i ? (v as number) : x)))}
                aria-label={`${labels[i]} stake`}
                sx={{ flex: 1 }}
              />
              <Typography variant="body2" sx={{ width: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {dollars(st[i])}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Tiles
        rows={views.map((v) => ({
          s: slipStats(v.outcomes, withStakes(bets, st)),
          base: changed ? slipStats(v.outcomes, bets) : undefined,
          color: v.color,
          name: v.name,
        }))}
        lead={{
          label: 'Total staked',
          value: `${dollars(total)}${changed ? ` (was ${dollars(start.reduce((t, v) => t + v, 0))})` : ''}`,
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
        <Button size="small" variant="contained" disabled={!changed} onClick={() => onApply(st)}>
          Use these stakes
        </Button>
        <Button size="small" disabled={!changed} onClick={() => setSt(start)}>
          Reset
        </Button>
      </Stack>
    </>
  );
};

/** A way to stake the bets: its stats under each model shown (`per`), and `stats`, the numbers it's judged by. */
type Cand = { st: number[]; stats: SlipStats; per: SlipStats[] };
/** Comparing models, how a staking is judged: by one model, or by whichever is less optimistic about it. */
type Judge = 'base' | 'layered' | 'worse';

/** The numbers a staking is judged by: one model's, or the worse of the two on each measure. */
const judged = (per: SlipStats[], judge: Judge | null): SlipStats =>
  per.length === 1 || !judge
    ? per[0]
    : judge === 'base'
      ? per[0]
      : judge === 'layered'
        ? per[1]
        : {
            ...per[0],
            ev: Math.min(per[0].ev, per[1].ev),
            pProfit: Math.min(per[0].pProfit, per[1].pProfit),
            p50: Math.min(per[0].p50, per[1].p50),
            worst: Math.min(per[0].worst, per[1].worst),
          };

/** Every way (or a large sample of ways) to stake the usable bets within the budget and caps, with its stats. */
const useCandidates = (props: ExploreProps, judge: Judge | null): Cand[] => {
  const { bets, budget, groups, usable } = props;
  const views = useModelViews(props);
  const raw = useMemo(
    () =>
      stakeCandidates(bets, usable, budget, groups).map((st) => ({
        st,
        per: views.map((v) => slipStats(v.outcomes, withStakes(bets, st))),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.outcomes, props.compare, budget, groups.join(), usable.join(), bets.map((b) => b.odds).join()],
  );
  return useMemo(() => raw.map((c) => ({ ...c, stats: judged(c.per, judge) })), [raw, judge]);
};

/** Comparing: how to judge stakings (by one model, or the worse of the two). */
const JudgeToggle: React.FC<{ value: Judge; onChange: (j: Judge) => void; label?: string }> = ({
  value,
  onChange,
  label = 'Judge by',
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
    <Typography variant="body2">{label}</Typography>
    <SegmentedToggle
      aria-label={label}
      size="small"
      value={value}
      onChange={(v: string) => onChange(v as Judge)}
      options={[
        { value: 'base', label: 'Base' },
        { value: 'layered', label: 'Track Specialists' },
        { value: 'worse', label: 'Worse of the two' },
      ]}
    />
  </Stack>
);

/** A suggested slip: the stakes against yours, its stats and a button to use it. */
const SlipSummary: React.FC<{
  c: Cand;
  bets: PricedBet[];
  labels: string[];
  outcomes: SlipOutcome[];
  onApply: (st: number[]) => void;
  views?: ModelView[];
}> = ({ c, bets, labels, outcomes, onApply, views }) => {
  const likely = likelyOutcomes(outcomes, 3, withStakes(bets, c.st));
  const comparing = !!views && views.length > 1;
  return (
    <Box>
      {bets.map((b, i) => (
        <Stack key={i} direction="row" justifyContent="space-between" spacing={1} sx={{ opacity: c.st[i] ? 1 : 0.5 }}>
          <Typography
            variant="body2"
            noWrap
            title={labels[i]}
            sx={{ textDecoration: c.st[i] ? 'none' : 'line-through', minWidth: 0 }}
          >
            {labels[i]}
          </Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
            {c.st[i] ? dollars(c.st[i]) : '—'}
            {c.st[i] !== b.stake && (
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}
                (yours {dollars(b.stake)})
              </Typography>
            )}
          </Typography>
        </Stack>
      ))}
      {comparing ? (
        views!.map((v, i) => (
          <Typography key={v.key} variant="body2" sx={{ mt: i === 0 ? 1 : 0 }}>
            <Swatch c={v.color!} />
            {v.name}: expected <b>{money(c.per[i].ev)}</b> · chance of profit <b>{pct(c.per[i].pProfit)}</b> · worst{' '}
            {money(c.per[i].worst)}
          </Typography>
        ))
      ) : (
        <>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Expected <b>{money(c.stats.ev)}</b> on {dollars(c.stats.staked)} · chance of profit{' '}
            <b>{pct(c.stats.pProfit)}</b>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Typical {money(c.stats.p50)} · worst {money(c.stats.worst)} · most likely:{' '}
            {likely.map((o) => `${pct(o.prob)} ${money(o.net)}`).join(' · ')}
          </Typography>
        </>
      )}
      <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={() => onApply(c.st)}>
        Use this slip
      </Button>
    </Box>
  );
};

/** 5. Choose your risk: slide along the best trade-offs between expected profit and chance of profit. */
export const RiskTab: React.FC<ExploreProps> = (props) => {
  const { outcomes, bets, labels, onApply } = props;
  const k = useChartTokens();
  const theme = useTheme();
  const views = useModelViews(props);
  // Comparing: judged by the worse of the two models unless you pick one.
  const [judge, setJudge] = useState<Judge>('worse');
  const cands = useCandidates(props, props.compare ? judge : null);
  const frontier = useMemo(() => riskFrontier(cands), [cands]);
  const [t, setT] = useState(0);
  const { ref, width: boxW } = useWidth(280, 560);
  if (frontier.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        No bets here can go into a suggested slip (they're all overpriced at these odds).
      </Typography>
    );
  const sel = frontier[Math.round((t / 100) * (frontier.length - 1))];
  const yours = judged(
    views.map((v) => slipStats(v.outcomes, bets)),
    props.compare ? judge : null,
  );
  // Thin the cloud of other slips for drawing; the line and the points that matter are always drawn.
  const dots = cands.length > 1500 ? cands.filter((_, i) => i % Math.ceil(cands.length / 1500) === 0) : cands;
  const W = Math.min(boxW, 600);
  const H = 240;
  const M = { l: 52, r: 14, t: 16, b: 34 };
  const pps = cands.map((c) => c.stats.pProfit);
  const evs = cands.map((c) => c.stats.ev);
  const xmin = Math.max(0, Math.min(...pps, yours.pProfit) - 0.03);
  const xmax = Math.min(1, Math.max(...pps, yours.pProfit) + 0.03);
  const ymin = Math.min(0, ...evs, yours.ev);
  const ymax = Math.max(...evs, yours.ev) * 1.1 || 1;
  const x = (p: number) => M.l + ((p - xmin) / (xmax - xmin || 1)) * (W - M.l - M.r);
  const y = (v: number) => M.t + (1 - (v - ymin) / (ymax - ymin || 1)) * (H - M.t - M.b);
  const xTicks = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].filter((p) => p > xmin && p < xmax);
  const yStep = Math.max(1, Math.ceil((ymax - ymin) / 4 / 5) * 5);
  const yTicks: number[] = [];
  for (let v = Math.ceil(ymin / yStep) * yStep; v <= ymax; v += yStep) yTicks.push(v);
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Slide from the most value to the steadiest along the best ways to stake these bets (same money, no more than 40%
        on bets that win and lose together, never overpriced bets).
      </Typography>
      {props.compare && (
        <>
          <JudgeToggle value={judge} onChange={setJudge} />
          {judge === 'worse' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5, mb: 1.5 }}>
              Each way of staking is scored by whichever model is less optimistic about it, so the slip you pick holds
              up if either model is the right one.
            </Typography>
          )}
        </>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box ref={ref} sx={{ flex: '1 1 60%', minWidth: 0 }}>
          <svg
            width={W}
            height={H}
            style={{ display: 'block' }}
            role="img"
            aria-label="Expected profit against chance of profit"
          >
            {xTicks.map((p) => (
              <g key={p}>
                <line x1={x(p)} x2={x(p)} y1={M.t} y2={H - M.b} stroke={k.grid} />
                <text x={x(p)} y={H - M.b + 14} textAnchor="middle" fontSize={11} fill={k.axisText}>
                  {pct(p)}
                </text>
              </g>
            ))}
            {yTicks.map((v) => (
              <g key={v}>
                <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} stroke={k.grid} />
                <text x={M.l - 6} y={y(v)} dy="0.32em" textAnchor="end" fontSize={11} fill={k.axisText}>
                  {money(v)}
                </text>
              </g>
            ))}
            <text x={(M.l + W - M.r) / 2} y={H - 4} textAnchor="middle" fontSize={11} fill={k.axisText}>
              chance of profit →
            </text>
            <text
              transform={`translate(12 ${(M.t + H - M.b) / 2}) rotate(-90)`}
              textAnchor="middle"
              fontSize={11}
              fill={k.axisText}
            >
              expected profit →
            </text>
            {dots.map((c, i) => (
              <circle key={i} cx={x(c.stats.pProfit)} cy={y(c.stats.ev)} r={1.6} fill={k.axisText} opacity={0.22} />
            ))}
            <polyline
              points={frontier.map((c) => `${x(c.stats.pProfit)},${y(c.stats.ev)}`).join(' ')}
              fill="none"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
            />
            {/* Each point on the line can be picked directly, with a target bigger than the mark. */}
            {frontier.map((c, i) => (
              <circle
                key={`f${i}`}
                cx={x(c.stats.pProfit)}
                cy={y(c.stats.ev)}
                r={8}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={() => setT(frontier.length > 1 ? (i / (frontier.length - 1)) * 100 : 0)}
              >
                <title>{`${money(c.stats.ev)} expected · ${pct(c.stats.pProfit)} chance of profit`}</title>
              </circle>
            ))}
            <circle cx={x(yours.pProfit)} cy={y(yours.ev)} r={6} fill="none" stroke={k.text} strokeWidth={2} />
            <text
              x={x(yours.pProfit) + (x(yours.pProfit) > W - 90 ? -9 : 9)}
              y={y(yours.ev) - 10}
              textAnchor={x(yours.pProfit) > W - 90 ? 'end' : 'start'}
              fontSize={11}
              fill={k.text}
            >
              your slip
            </text>
            <circle
              cx={x(sel.stats.pProfit)}
              cy={y(sel.stats.ev)}
              r={7}
              fill={theme.palette.primary.main}
              stroke={k.surface}
              strokeWidth={2}
            />
          </svg>
          <Typography variant="caption" color="text.secondary">
            Each dot is one way to stake these bets; the line is the best of them. Click a point on the line to pick it.
          </Typography>
        </Box>
        <Box sx={{ flex: '1 1 40%', minWidth: 240 }}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">Most value</Typography>
            <Typography variant="caption">Steadiest</Typography>
          </Stack>
          <Slider value={t} onChange={(_, v) => setT(v as number)} aria-label="Risk" sx={{ mb: 1 }} />
          <SlipSummary c={sel} bets={bets} labels={labels} outcomes={outcomes} onApply={onApply} views={views} />
        </Box>
      </Stack>
    </>
  );
};

/** 6. Stake to a goal: the most expected profit that meets your limits. */
export const GoalsTab: React.FC<ExploreProps> = (props) => {
  const { outcomes, bets, labels, onApply } = props;
  const views = useModelViews(props);
  // Comparing: the limits must hold under both models unless you pick one.
  const [under, setUnder] = useState<'both' | 'base' | 'layered'>('both');
  const cands = useCandidates(props, props.compare ? (under === 'both' ? 'worse' : under) : null);
  // The models a limit is checked against (comparing: one, or both).
  const checkViews = !props.compare ? [0] : under === 'both' ? [0, 1] : under === 'base' ? [0] : [1];
  const [maxLoss, setMaxLoss] = useState('');
  const [minP, setMinP] = useState('');
  const [winX, setWinX] = useState('');
  const [winP, setWinP] = useState('');
  const num = (s: string) => (s.trim() === '' ? null : Number(s));
  const limits = { maxLoss: num(maxLoss), minP: num(minP), winX: num(winX), winP: num(winP) };
  const any = Object.values(limits).some((v) => v !== null);
  const best = useMemo(() => {
    if (!any) return null;
    const ok = (c: Cand) =>
      (limits.maxLoss === null || -c.stats.worst <= limits.maxLoss + 1e-9) &&
      (limits.minP === null || c.stats.pProfit * 100 >= limits.minP - 1e-9) &&
      (limits.winX === null ||
        limits.winP === null ||
        checkViews.every(
          (vi) =>
            chanceOfAtLeast(views[vi].outcomes, withStakes(bets, c.st), limits.winX!) * 100 >= limits.winP! - 1e-9,
        ));
    return cands.filter(ok).sort((a, b) => b.stats.ev - a.stats.ev || b.stats.pProfit - a.stats.pProfit)[0] ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cands, maxLoss, minP, winX, winP, under]);
  const field = (label: string, v: string, set: (s: string) => void, unit: string, w: number) => (
    <TextField
      size="small"
      label={label}
      value={v}
      onChange={(e) => set(e.target.value.replace(/[^0-9.]/g, ''))}
      sx={{ width: w }}
      InputProps={{
        [unit === '$' ? 'startAdornment' : 'endAdornment']: (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            {unit}
          </Typography>
        ),
      }}
      inputProps={{ inputMode: 'decimal' }}
    />
  );
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Set any of these limits and get the slip with the most expected profit that meets them (same money and rules as
        the suggestions).
      </Typography>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
        {field('Never lose more than', maxLoss, setMaxLoss, '$', 180)}
        {field('Chance of profit at least', minP, setMinP, '%', 200)}
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Win at least
          </Typography>
          {field('Amount', winX, setWinX, '$', 110)}
          <Typography variant="body2" color="text.secondary">
            in
          </Typography>
          {field('Share of races', winP, setWinP, '%', 175)}
        </Stack>
        {props.compare && (
          <SegmentedToggle
            aria-label="Limits hold under"
            size="small"
            value={under}
            onChange={(v: string) => setUnder(v as 'both' | 'base' | 'layered')}
            options={[
              { value: 'both', label: 'Under both models' },
              { value: 'base', label: 'Base' },
              { value: 'layered', label: 'Track Specialists' },
            ]}
          />
        )}
      </Stack>
      {!any ? (
        <Typography variant="body2" color="text.secondary">
          Set a limit above to see a slip.
        </Typography>
      ) : best ? (
        <Box sx={{ p: 1.5, border: 2, borderColor: 'primary.main', borderRadius: 1.5, maxWidth: 560 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Best slip that meets your limits{props.compare && under === 'both' ? ' under both models' : ''}
          </Typography>
          <SlipSummary c={best} bets={bets} labels={labels} outcomes={outcomes} onApply={onApply} views={views} />
        </Box>
      ) : (
        <Typography variant="body2" color="warning.main">
          No way to stake these bets meets all of those limits. Loosen one (for example a lower chance of profit).
        </Typography>
      )}
    </>
  );
};

export const EXPLORE_TABS: { value: ExploreTab; label: string }[] = [
  { value: 'whatif', label: 'What if' },
  { value: 'contribution', label: 'Each bet' },
  { value: 'stakes', label: 'Stakes' },
  { value: 'risk', label: 'Risk' },
  { value: 'goals', label: 'Goals' },
];

/** The chosen exploring tab. */
export const SlipExplore: React.FC<ExploreProps & { tab: ExploreTab }> = ({ tab, ...p }) =>
  tab === 'whatif' ? (
    <WhatIfTab {...p} />
  ) : tab === 'contribution' ? (
    <ContributionTab {...p} />
  ) : tab === 'stakes' ? (
    <StakesTab {...p} />
  ) : tab === 'risk' ? (
    <RiskTab {...p} />
  ) : (
    <GoalsTab {...p} />
  );
